<template>
  <div class="facilities-panel">
    <div class="facilities-toolbar">
      <div class="facilities-search">
        <i class="fa fa-search"></i>
        <input
          v-model="search"
          class="input"
          type="text"
          placeholder="Kërko objekt..."
        />
      </div>
      <button
        v-if="canEdit"
        type="button"
        class="button is-small facilities-add-btn"
        :disabled="creating || saving"
        @click="startCreate"
      >
        <i class="fa fa-plus mr-1"></i>
        Shto objekt
      </button>
    </div>

    <form v-if="creating" class="facilities-editor" @submit.prevent="submitCreate">
      <label>Emri i objektit të ri</label>
      <div class="facilities-editor-row">
        <input
          ref="createInput"
          v-model="createName"
          class="input"
          type="text"
          maxlength="190"
          placeholder="p.sh. Materiale"
          :disabled="saving"
          @keydown.esc.prevent="cancelCreate"
        />
        <button type="submit" class="button is-small is-link" :disabled="saving || !trimmedCreateName">
          {{ saving ? "Duke ruajtur..." : "Krijo" }}
        </button>
        <button type="button" class="button is-small" :disabled="saving" @click="cancelCreate">Anulo</button>
      </div>
    </form>

    <div v-if="loading" class="facilities-empty">
      <i class="fa fa-spinner fa-spin mr-2"></i>
      Duke ngarkuar objektet...
    </div>
    <div v-else-if="!filteredFacilities.length" class="facilities-empty">
      {{ search.trim() ? "Nuk u gjet asnjë objekt me këtë emër." : "Nuk ka objekte të magazinës." }}
    </div>
    <ul v-else class="facilities-list">
      <li v-for="facility in filteredFacilities" :key="facility.id" class="facilities-row">
        <div class="facilities-row-main">
          <div class="facilities-row-icon">
            <i class="fa-regular fa-building"></i>
          </div>
          <div class="facilities-row-copy">
            <form v-if="editingId === facility.id" class="facilities-editor-row" @submit.prevent="submitEdit">
              <input
                ref="editInput"
                v-model="editName"
                class="input"
                type="text"
                maxlength="190"
                :disabled="saving"
                @keydown.esc.prevent="cancelEdit"
              />
              <button type="submit" class="button is-small is-link" :disabled="saving || !trimmedEditName">
                {{ saving ? "Duke ruajtur..." : "Ruaj" }}
              </button>
              <button type="button" class="button is-small" :disabled="saving" @click="cancelEdit">Anulo</button>
            </form>
            <template v-else>
              <div class="facilities-row-name">{{ facility.name }}</div>
              <div class="facilities-row-meta">{{ warehouseCountLabel(facility.id) }}</div>
            </template>
          </div>
        </div>
        <button
          v-if="canEdit && editingId !== facility.id"
          type="button"
          class="button is-small facilities-edit-btn"
          :disabled="saving || creating"
          @click="startEdit(facility)"
        >
          <i class="fa fa-pencil mr-1"></i>
          Ndrysho
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import WarehousesServices from "@/services/Warehouse";
import permissionMixin from "@/mixins/permissions-mixin.js";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      loading: true,
      saving: false,
      creating: false,
      search: "",
      createName: "",
      editName: "",
      editingId: null,
      facilities: [],
      warehouseCounts: {},
    };
  },
  computed: {
    canEdit() {
      return this.am_allowed("transactions.can_edit_warehouses");
    },
    trimmedCreateName() {
      return (this.createName || "").trim();
    },
    trimmedEditName() {
      return (this.editName || "").trim();
    },
    filteredFacilities() {
      const query = this.search.trim().toLowerCase();
      const rows = [...this.facilities].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "sq", { sensitivity: "base" })
      );
      if (!query) return rows;
      return rows.filter((item) => String(item.name || "").toLowerCase().includes(query));
    },
  },
  methods: {
    warehouseCountLabel(facilityId) {
      const count = this.warehouseCounts[Number(facilityId)] || 0;
      if (count === 0) return "Pa magazina të lidhura";
      return count === 1 ? "1 magazinë e lidhur" : `${count} magazina të lidhura`;
    },
    focusRef(name) {
      this.$nextTick(() => {
        const el = this.$refs[name];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) input.focus();
        if (input && input.select) input.select();
      });
    },
    startCreate() {
      this.cancelEdit();
      this.creating = true;
      this.createName = "";
      this.focusRef("createInput");
    },
    cancelCreate() {
      this.creating = false;
      this.createName = "";
    },
    startEdit(facility) {
      this.cancelCreate();
      this.editingId = facility.id;
      this.editName = facility.name || "";
      this.focusRef("editInput");
    },
    cancelEdit() {
      this.editingId = null;
      this.editName = "";
    },
    duplicateName(name, ignoreId = null) {
      const needle = name.toLowerCase();
      return this.facilities.some((item) => {
        if (ignoreId != null && Number(item.id) === Number(ignoreId)) return false;
        return String(item.name || "").trim().toLowerCase() === needle;
      });
    },
    async submitCreate() {
      const name = this.trimmedCreateName;
      if (!name || this.saving) return;
      if (this.duplicateName(name)) {
        this.$toast.error("Ekziston tashmë një objekt me këtë emër.");
        return;
      }
      this.saving = true;
      try {
        const created = await WarehousesServices.createWarehouseFacility({ name });
        this.facilities = [...this.facilities, { ...created, label: created.name }];
        this.cancelCreate();
        this.$toast.success("Objekti u krijua.");
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Objekti nuk u krijua.");
      } finally {
        this.saving = false;
      }
    },
    async submitEdit() {
      const name = this.trimmedEditName;
      if (!name || this.saving || this.editingId == null) return;
      if (this.duplicateName(name, this.editingId)) {
        this.$toast.error("Ekziston tashmë një objekt me këtë emër.");
        return;
      }
      this.saving = true;
      try {
        const updated = await WarehousesServices.editWarehouseFacility({
          id: this.editingId,
          name,
        });
        this.facilities = this.facilities.map((item) =>
          Number(item.id) === Number(updated.id) ? { ...item, ...updated, label: updated.name } : item
        );
        this.cancelEdit();
        this.$toast.success("Objekti u modifikua.");
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Objekti nuk u modifikua.");
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    try {
      const [facilities, warehouses] = await Promise.all([
        WarehousesServices.getWarehouseFacilities(),
        WarehousesServices.getWarehouses().catch(() => []),
      ]);
      this.facilities = facilities || [];
      const counts = {};
      (warehouses || []).forEach((warehouse) => {
        const id = Number(warehouse.warehouse_facility);
        if (!id) return;
        counts[id] = (counts[id] || 0) + 1;
      });
      this.warehouseCounts = counts;
    } catch (error) {
      this.$toast.error(error?.response?.data?.message || "Objektet nuk u ngarkuan.");
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.facilities-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.facilities-search {
  position: relative;
  flex: 1;
  min-width: 0;
}
.facilities-search i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 12px;
}
.facilities-search .input {
  padding-left: 28px;
  height: 34px;
  border-radius: 8px;
}
.facilities-add-btn,
.facilities-edit-btn {
  border-radius: 8px;
  font-weight: 600;
}
.facilities-add-btn {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}
.facilities-add-btn:hover {
  background: #1e40af;
  border-color: #1e40af;
  color: #fff;
}
.facilities-editor {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
}
.facilities-editor > label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
}
.facilities-editor-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.facilities-editor-row .input {
  flex: 1;
  min-width: 160px;
  height: 32px;
}
.facilities-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  max-height: 58vh;
  overflow-y: auto;
}
.facilities-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
}
.facilities-row:last-child {
  border-bottom: 0;
}
.facilities-row:hover {
  background: #f8fafc;
}
.facilities-row-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}
.facilities-row-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.facilities-row-copy {
  min-width: 0;
  flex: 1;
}
.facilities-row-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.facilities-row-meta {
  font-size: 12px;
  color: #64748b;
}
.facilities-empty {
  padding: 28px 12px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
  background: #f8fafc;
  border: 1px dashed #dbe3ee;
  border-radius: 12px;
}
</style>
